import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState } from "../store";
import { ROLES } from "../constants/roles";
import { ROUTES } from "../routes/constants";

interface UseSharePointValidationReturn {
  showValidationModal: boolean;
  shouldRedirectToSetup: boolean;
  handleSignOut: () => void;
}

export const useSharePointValidation = (): UseSharePointValidationReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [shouldRedirectToSetup, setShouldRedirectToSetup] = useState(false);

  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setShowValidationModal(false);
      setShouldRedirectToSetup(false);
      return;
    }

    const userRole = user.roles[0]?.name;

    // Bypass validation for Super Admin
    if (userRole === ROLES.SUPER_ADMIN) {
      setShowValidationModal(false);
      setShouldRedirectToSetup(false);
      return;
    }

    if (location.pathname === ROUTES.AUTH.SHAREPOINT_SETUP) {
      setShowValidationModal(false);
      setShouldRedirectToSetup(false);
      return;
    }

    if (!user.sharePointCredsValid) {
      if (userRole === ROLES.ARCHIVIST) {
        setShouldRedirectToSetup(true);
        setShowValidationModal(false);
      } else {
        setShowValidationModal(true);
        setShouldRedirectToSetup(false);
      }
    } else {
      setShowValidationModal(false);
      setShouldRedirectToSetup(false);
    }
  }, [user, isAuthenticated, location.pathname]);

  useEffect(() => {
    if (shouldRedirectToSetup) {
      navigate(ROUTES.AUTH.SHAREPOINT_SETUP, { replace: true });
    }
  }, [shouldRedirectToSetup, navigate]);

  const handleSignOut = () => {
    navigate(ROUTES.AUTH.LOGIN, { replace: true });
  };

  return {
    showValidationModal,
    shouldRedirectToSetup,
    handleSignOut,
  };
};
